'use client'

import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { X } from 'lucide-react'
import { Button } from './button'
import {
  Popover, PopoverAnchor, PopoverContent,
} from './popover'

const KEY_INTRO_SEEN = 'overlay-hint-intro-seen'
const KEY_SWITCHED_SEEN = 'overlay-hint-switched-seen'
const KEY_DISMISS_ALL = 'overlay-hint-permanent-dismiss'

export type CoachmarkVariant = 'intro' | 'switched' | null

function readFlag(key: string): boolean {
  if (typeof window === 'undefined') return true // SSR: assume seen, will re-check on mount
  try { return window.localStorage.getItem(key) === '1' } catch { return true }
}

function writeFlag(key: string): void {
  try { window.localStorage.setItem(key, '1') } catch { /* ignore */ }
}

/**
 * Gerencia os 2 coachmarks de DialogSidebar:
 * 1. `intro` — aparece na 1ª vez que o overlay abre
 * 2. `switched` — aparece na 1ª vez que o usuário troca o modo
 *
 * Ambos respeitam a flag `overlay-hint-permanent-dismiss` ("Não mostrar mais").
 */
export function useOverlayCoachmarks(open: boolean) {
  const [variant, setVariant] = useState<CoachmarkVariant>(null)

  useEffect(() => {
    if (!open) {
      setVariant(null)
      return
    }
    if (readFlag(KEY_DISMISS_ALL) || readFlag(KEY_INTRO_SEEN)) return
    // Pequeno delay pra dar tempo do overlay terminar de animar antes do popover abrir
    const t = setTimeout(() => setVariant('intro'), 400)
    return () => clearTimeout(t)
  }, [open])

  const onModeChanged = useCallback(() => {
    if (readFlag(KEY_DISMISS_ALL) || readFlag(KEY_SWITCHED_SEEN)) return
    setVariant('switched')
  }, [])

  const dismiss = useCallback((permanent = false) => {
    setVariant((prev) => {
      if (prev === 'intro') writeFlag(KEY_INTRO_SEEN)
      if (prev === 'switched') writeFlag(KEY_SWITCHED_SEEN)
      if (permanent) writeFlag(KEY_DISMISS_ALL)
      return null
    })
  }, [])

  return { variant, onModeChanged, dismiss }
}

interface OverlayCoachmarkProps {
  variant: CoachmarkVariant
  onDismiss: (permanent?: boolean) => void
  /** Elemento HTML que serve de âncora (geralmente o ModeToggle wrapper). */
  children: ReactNode
}

/**
 * Wrapper que renderiza um Popover anchored nos children. Quando `variant !== null`,
 * mostra o coachmark correspondente. Use envolvendo o ModeToggle.
 */
export function OverlayCoachmark({ variant, onDismiss, children }: OverlayCoachmarkProps) {
  // Auto-dismiss em 8s
  useEffect(() => {
    if (variant == null) return
    const t = setTimeout(() => onDismiss(false), 8000)
    return () => clearTimeout(t)
  }, [variant, onDismiss])

  const open = variant != null

  const title = variant === 'intro'
    ? 'Modal ou painel lateral'
    : 'Pronto, modo trocado'

  const body = variant === 'intro'
    ? 'Você pode alternar entre modal e painel lateral conforme preferir. A escolha fica salva.'
    : 'Sempre que quiser voltar ao outro modo, é só clicar aqui.'

  return (
    <Popover open={open}>
      <PopoverAnchor asChild>
        <div className="relative inline-flex">{children}</div>
      </PopoverAnchor>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        className="w-72 p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()} // não fecha por click fora; só pelos botões
        onEscapeKeyDown={() => onDismiss(false)}
      >
        <div className="px-3 py-2.5 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="text-xs font-semibold">{title}</div>
            <button
              type="button"
              aria-label="Fechar dica"
              onClick={() => onDismiss(false)}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">{body}</p>
          <div className="flex items-center justify-between gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 text-[11px] text-muted-foreground hover:text-foreground"
              onClick={() => onDismiss(true)}
            >
              Não mostrar mais
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-6 text-[11px]"
              onClick={() => onDismiss(false)}
            >
              Ok
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
